import LabeledSlider from "@/components/LabeledSlider.tsx";
import {Button} from "@/components/ui/button.tsx";
import {Input} from "@/components/ui/input.tsx";
import {Label} from "@/components/ui/label.tsx";
import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover.tsx";
import {Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue}
    from "@/components/ui/select.tsx";
import CharacterAvatar from "@/characters/CharacterAvatar.tsx";
import {useCombatants} from "@/encounter/combatants/CombatantHooks.ts";
import {useDungeon} from "@/encounter/dungeon/DungeonHooks.ts";
import {useAreaEffect} from "@/encounter/effect/AreaEffectHooks.ts";
import {useEncounterViewPort} from "@/encounter/viewport/EncounterViewPortHooks.ts";
import {cn} from "@/lib/utils.ts";
import {useTableTopSelection} from "@/tabletop/selection/TableTopSelectionHooks.ts";
import {getAreaEffectWorldBounds} from "@/tabletop/effects/AreaEffectGeometry.ts";
import {Eye, EyeOff, LockOpen, Sparkles, Trash2} from "lucide-react";
import {HexColorPicker} from "react-colorful";
import {useMapPopoverLayout} from "@/tabletop/useMapPopoverLayout.ts";
import {numberedAreaEffects} from "./AreaEffectHighlights.ts";

export default function AreaEffectOptions({viewport}: {viewport: {width: number; height: number}}) {
    const {effects, updateEffect, removeEffect} = useAreaEffect();
    const {selectedEffect, clearSelection} = useTableTopSelection();
    const {combatants} = useCombatants();
    const {dungeon, dungeonSettings} = useDungeon();
    const {mapTransform} = useEncounterViewPort();
    const combatant = combatants.find(candidate => candidate.id === selectedEffect?.combatantId);
    const bounds = selectedEffect ? getAreaEffectWorldBounds(selectedEffect, combatant?.position ?? selectedEffect.position,
        combatant?.rotation ?? selectedEffect.rotation ?? 0) : {left: 0, right: 0, top: 0, bottom: 0};
    const screenBounds = {
        left: mapTransform.x + bounds.left * mapTransform.scale,
        right: mapTransform.x + bounds.right * mapTransform.scale,
        top: mapTransform.y + bounds.top * mapTransform.scale,
        bottom: mapTransform.y + bounds.bottom * mapTransform.scale,
    };
    const {panelRef, contentRef, style} = useMapPopoverLayout({anchor: screenBounds, viewport, width: 260, gap: 36});

    if (!selectedEffect || selectedEffect.locked) return null;

    const updateCombatant = (value: string) => {
        const source = combatants.find(candidate => candidate.id === value);
        const previousSource = combatants.find(candidate => candidate.id === selectedEffect.combatantId);
        const transform = source ?? previousSource;
        updateEffect({
            ...selectedEffect,
            combatantId: source?.id,
            levelId: transform?.levelId ?? selectedEffect.levelId,
            position: transform?.position ?? selectedEffect.position,
            rotation: transform?.rotation ?? selectedEffect.rotation,
        }, "immediate");
    };

    return <div className="pointer-events-none absolute inset-0 z-10">
        <div ref={panelRef} role="dialog" aria-label="Flächeneffekt bearbeiten"
            className="pointer-events-auto absolute overflow-y-auto rounded-lg border border-border bg-card p-3 shadow-xl"
            style={style}>
            <div ref={contentRef} className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                    <h3 className="min-w-0 truncate text-sm font-medium" title={selectedEffect.name || selectedEffect.type}>
                        {selectedEffect.name || "Flächeneffekt"}
                    </h3>
                    <div className="flex shrink-0 items-center gap-1">
                        <Button type="button" size="icon-sm" variant="ghost"
                            aria-label={selectedEffect.hidden ? "Anzeigen" : "Verbergen"}
                            title={selectedEffect.hidden ? "Anzeigen" : "Verbergen"}
                            onClick={() => updateEffect({...selectedEffect, hidden: !selectedEffect.hidden}, "immediate")}>
                            {selectedEffect.hidden ? <EyeOff/> : <Eye/>}
                        </Button>
                        <Button type="button" size="icon-sm" variant="ghost" aria-label="Sperren" title="Sperren"
                            onClick={() => updateEffect({...selectedEffect, locked: true})}>
                            <LockOpen/>
                        </Button>
                        <Button type="button" size="icon-sm" variant="ghost"
                            className="text-destructive hover:text-destructive" aria-label="Löschen" title="Löschen"
                            onClick={() => {void removeEffect(selectedEffect); clearSelection();}}>
                            <Trash2/>
                        </Button>
                    </div>
                </div>
                <div className="space-y-1">
                    <Button type="button" variant={selectedEffect.highlighted ? "secondary" : "outline"} size="sm"
                        className="w-full justify-start" aria-label="Betroffene Tokens hervorheben"
                        aria-pressed={Boolean(selectedEffect.highlighted)}
                        onClick={() => updateEffect({...selectedEffect, highlighted: !selectedEffect.highlighted}, "immediate")}>
                        <Sparkles/>
                        <span className="grow text-left">Tokens hervorheben</span>
                        <span className="rounded px-1.5 text-xs font-bold text-white [text-shadow:0_1px_2px_black]"
                            style={{backgroundColor: selectedEffect.color}}>
                            {numberedAreaEffects(effects, combatants, dungeonSettings.levelId)
                                .find(({effect}) => effect.id === selectedEffect.id)?.number}
                        </span>
                    </Button>
                    {selectedEffect.highlighted && selectedEffect.hidden &&
                        <p className="text-xs text-muted-foreground">Die Hervorhebung ist aktiv, sobald die Fläche sichtbar ist.</p>}
                </div>
                <div className="space-y-1">
                    <div className="flex items-center justify-between">
                        <Label htmlFor="effect-name" className="text-xs">Name</Label>
                        <span className="text-xs text-muted-foreground">{selectedEffect.type}</span>
                    </div>
                    <Input id="effect-name" className="h-8" value={selectedEffect.name}
                        onChange={event => updateEffect({...selectedEffect, name: event.target.value})}/>
                </div>
                <div className="flex items-center justify-between">
                    <Label htmlFor="effect-color" className="text-xs">Farbe</Label>
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button type="button" id="effect-color" variant="outline" size="sm" className="w-24"
                                style={{backgroundColor: selectedEffect.color}}>
                                {selectedEffect.color.toUpperCase()}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent align="end" className="w-fit rounded-xl p-2 text-sm">
                            <HexColorPicker color={selectedEffect.color}
                                onChange={color => updateEffect({...selectedEffect, color})}/>
                        </PopoverContent>
                    </Popover>
                </div>
                <LabeledSlider label="Transparenz" valueLabel={`${selectedEffect.opacity}`}
                    value={selectedEffect.opacity} min={0.1} max={1} step={0.01}
                    onChange={opacity => updateEffect({...selectedEffect, opacity})}/>
                {selectedEffect.angle !== undefined &&
                    <LabeledSlider label="Winkel" valueLabel={`${selectedEffect.angle}°`}
                        value={selectedEffect.angle} min={1} max={360}
                        onChange={angle => updateEffect({...selectedEffect, angle})}/>}
                <div className="space-y-1">
                    <Label htmlFor="effect-source" className="text-xs">Quelle</Label>
                    <Select value={selectedEffect.combatantId ?? "none"} onValueChange={updateCombatant}>
                        <SelectTrigger id="effect-source" className="w-full"><SelectValue/></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="none">Keine</SelectItem>
                            <SelectGroup>
                                <SelectLabel>Sichtbar</SelectLabel>
                                {combatants.filter(item => !item.hidden && item.levelId === dungeonSettings.levelId)
                                    .map(item => <SelectItem key={item.id} value={item.id}
                                        className={cn("flex grow items-center p-0.5",
                                            item.hostile ? "bg-rose-950" : "bg-teal-950")}>
                                        <CharacterAvatar combatant={item} size={24}/>{item.name}
                                    </SelectItem>)}
                            </SelectGroup>
                            <SelectGroup>
                                <SelectLabel>Unsichtbar</SelectLabel>
                                {combatants.filter(item => item.hidden && item.levelId === dungeonSettings.levelId)
                                    .map(item => <SelectItem key={item.id} value={item.id}
                                        className={cn("flex grow items-center p-0.5",
                                            item.hostile ? "bg-rose-950" : "bg-teal-950")}>
                                        <CharacterAvatar combatant={item} size={24}/>{item.name}
                                    </SelectItem>)}
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                </div>
                {!selectedEffect.combatantId && <div className="space-y-1">
                    <Label htmlFor="effect-level" className="text-xs">Level</Label>
                    <Select value={selectedEffect.levelId ?? dungeonSettings.levelId}
                        onValueChange={levelId => updateEffect({...selectedEffect, levelId}, "immediate")}>
                        <SelectTrigger id="effect-level" className="w-full">
                            <SelectValue placeholder="Level wählen"/>
                        </SelectTrigger>
                        <SelectContent>
                            {[...dungeon.levels].sort((a, b) => a.sortIndex - b.sortIndex).map(level =>
                                <SelectItem key={level.id} value={level.id}>{level.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>}
            </div>
        </div>
    </div>;
}
